import { cn } from '@/utils'
import type { WireColor } from '@/api/types/color'
import { tintedPaletteHex } from '@/lib/colors/resolve-palette-color'
import { isNoneColor } from '@/lib/colors/fill-paint'
import colorNoneSvg from '@/assets/color-none.svg'
import { useColorSwatchDrag } from '../hooks/use-color-swatch-drag'

interface ColorSwatchProps {
  color: WireColor
  /** The tint this swatch shows and applies (a Recently used entry keeps its tint); default 100. */
  tint?: number
  isSelected?: boolean
  isStyleColor?: boolean
  onClick?: (color: WireColor, tint: number) => void
  /** A drag-to-fill started from this swatch. */
  onDragStart?: (color: WireColor, tint: number) => void
}

export function ColorSwatch({
  color,
  tint = 100,
  isSelected,
  isStyleColor,
  onClick,
  onDragStart,
}: ColorSwatchProps) {
  const isNone = isNoneColor(color)
  const { onPointerDown, onPointerMove, onPointerUp, onPointerCancel } = useColorSwatchDrag(
    color,
    tint,
    undefined,
    () => onDragStart?.(color, tint)
  )
  const label = tint === 100 ? color.colorName : `${color.colorName} ${tint}%`

  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={isSelected}
      className={cn(
        'relative overflow-visible flex items-center justify-center size-8 p-1 rounded-md shrink-0 bg-popover border-2 transition-colors',
        isSelected ? 'border-primary' : 'border-muted hover:bg-accent/50'
      )}
      onClick={() => onClick?.(color, tint)}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
    >
      {isStyleColor && (
        <span className="absolute top-0 right-0 size-2 rounded-full bg-primary ring-2 ring-white z-10" />
      )}
      {isNone ? (
        <div className="relative shrink-0 size-6">
          <img
            src={colorNoneSvg}
            alt="None"
            className="absolute inset-0 block max-w-none size-full"
            draggable={false}
          />
        </div>
      ) : (
        <span
          className="block size-6 rounded-[2px] border border-foreground shrink-0"
          style={{ backgroundColor: tintedPaletteHex(color, tint) ?? color.hexValue }}
        />
      )}
    </button>
  )
}
